import React, { useState } from "react";
import { Search, Sparkles, UserPlus } from "lucide-react";
import { lookupGuest } from "../lib/api";
import useReveal from "../hooks/useReveal";

export default function GuestLookup() {
  const [ref, visible] = useReveal();
  const [name, setName] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return;
    setLoading(true);
    try {
      const matches = await lookupGuest(name.trim());
      setResults(matches);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 md:py-32 px-6 bg-[#ECE3D4]">
      <div ref={ref} className={`max-w-[720px] mx-auto text-center reveal ${visible ? "is-visible" : ""}`}>
        <p className="text-[12px] uppercase tracking-widest-2 text-[#6b6157]">Are you on the list?</p>
        <h2 className="mt-4 font-serif-display text-[#9E2B25] text-[44px] md:text-[60px] leading-none">
          Find Your Invitation
        </h2>
        <p className="mt-6 text-[#4a443d] leading-relaxed max-w-lg mx-auto">
          Enter your name to confirm your invitation and see whether a plus-one is included.
        </p>

        <form onSubmit={search} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Type your full name..."
            className="flex-1 bg-[#F9F5EE] border border-[#211E1A]/20 rounded-sm px-4 py-3 text-[#211E1A] placeholder:text-[#9c9488] focus:outline-none focus:border-[#9E2B25] transition-colors"
          />
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 bg-[#211E1A] text-[#F4EFE6] text-[12px] uppercase tracking-widest-2 px-7 py-3 rounded-sm hover:bg-[#9E2B25] transition-colors disabled:opacity-60"
          >
            <Search size={15} /> {loading ? "Searching" : "Search"}
          </button>
        </form>

        {results !== null && (
          <div className="mt-10 text-left">
            {results.length === 0 ? (
              <div className="bg-[#F9F5EE] border border-[#211E1A]/10 rounded-sm p-6 text-center">
                <p className="text-[#4a443d]">
                  We couldn’t find that name. Please double-check the spelling, or reach out to the couple directly.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {results.map((r, i) => (
                  <div key={i} className="bg-[#F9F5EE] border border-[#211E1A]/10 rounded-sm p-6">
                    <div className="flex items-start gap-3">
                      <Sparkles size={18} className="text-[#9E2B25] mt-1" />
                      <div>
                        <h3 className="font-serif-display text-2xl text-[#211E1A]">{r.name}</h3>
                        <p className="mt-1 text-[#4a443d]">You’re on the list — we can’t wait to celebrate with you!</p>
                        {r.plus_one ? (
                          <p className="mt-3 inline-flex items-center gap-2 text-[#9E2B25] text-sm">
                            <UserPlus size={15} />
                            Plus-one included{r.plus_one_name ? `: ${r.plus_one_name}` : ""}
                          </p>
                        ) : (
                          <p className="mt-3 text-[#6b6157] text-sm">This invitation is for you individually.</p>
                        )}
                        {r.note ? <p className="mt-2 text-[#6b6157] text-sm italic">{r.note}</p> : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
